<template>
  <MainHeader />
  <MainSidebar />
  <div
    class="main-content bg_gray d-flex flex-column transition overflow-hidden"
  >
    <BreadcrumbMenu
      pageTitle="create_purchase"
      :items="breadcrumbItems"
      :showHome="false"
    />
    <div class="row mb-40 purchase-form-grid">
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ $t("purchase_page.date") }}</label>
          <input
            type="date"
            v-model="form.date"
            class="w-100 h-55 bg_ash border-0 rounded-1 fs-14 text-black bg-white"
          />
        </div>
      </div>
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ $t("purchase_page.reference_no") }}</label>
          <input
            type="text"
            v-model="form.ref"
            class="w-100 h-55 bg_ash border-0 rounded-1 fs-14 text-black bg-white"
            :placeholder="$t('purchase_page.reference_no')"
          />
        </div>
      </div>
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ $t("purchase_page.supplier") }}</label>
          <select v-model="form.supplier_id" class="bg-white border-0 rounded-1 fs-14 text-optional">
            <option :value="null">{{ $t("purchase_page.choose_supplier") }}</option>
            <option v-for="supplier in suppliers" :key="supplier.id" :value="supplier.id">
              {{ supplier.name }}
            </option>
          </select>
        </div>
      </div>
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ $t("purchase_page.warehouse") }}</label>
          <select v-model="form.warehouse_id" class="bg-white border-0 rounded-1 fs-14 text-optional">
            <option :value="null">{{ $t("purchase_page.select_warehouse") }}</option>
            <option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse.id">
              {{ warehouse.name }}
            </option>
          </select>
        </div>
      </div>
      <div class="col-12 purchase-search-block">
        <div class="form-group mb-25 position-relative">
          <label class="d-block fs-14 text-black mb-2">{{ $t("purchase_page.choose_product") }}</label>
          <div class="search-area style-two position-relative w-100">
            <input
              type="text"
              v-model="searchQuery"
              @input="handleSearch"
              :placeholder="$t('purchase_page.scan_search_product')"
              class="w-100 h-55 bg_ash border-0 rounded-1 fs-14 text-black bg-white pe-3"
            />
            <button
              type="button"
              class="bg-transparent border-0 position-absolute h-100 pt-0 py-0 px-2 pointer-events-none"
              tabindex="-1"
              aria-hidden="true"
            >
              <img src="../../assets/img/icons/search.svg" alt="Image" />
            </button>
          </div>

          <!-- Search Results Dropdown -->
          <div v-if="showResults && searchResults.length > 0" class="search-results-dropdown position-absolute w-100 bg-white shadow-sm rounded-1 z-3 mt-1 overflow-auto">
            <div 
              v-for="product in searchResults" 
              :key="product.id"
              @click="addProduct(product)"
              class="search-result-item p-3 border-bottom cursor-pointer hover-bg-light"
              style="cursor: pointer;"
            >
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <div class="fw-bold fs-14 text-black">{{ product.name }}</div>
                  <div class="fs-12 text-muted">{{ product.code }}</div>
                </div>
                <div class="text-end">
                  <div class="text-primary fw-bold">${{ product.cost }}</div>
                  <div class="fs-12 text-muted">{{ $t("purchase_page.stock") }}: {{ getProductStock(product) }}</div>
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="showResults && normalizedSearchQuery.length >= 1" class="search-results-dropdown position-absolute w-100 bg-white shadow-sm rounded-1 z-3 mt-1 p-3 text-center text-muted">
            {{ $t("purchase_page.no_standard_products_found") }}
          </div>
        </div>
      </div>
    </div>
    <SelectedProducts :products="selectedProducts" @remove="removeProduct" />
    <SubmitPurchase 
      v-model:orderTax="form.order_tax"
      v-model:discount="form.discount"
      v-model:shipping="form.shipping"
      v-model:status="form.status"
      v-model:notes="form.notes"
      v-model:paidAmount="form.paid_amount"
      v-model:paymentMethodId="form.payment_method_id"
      :paymentMethods="paymentMethods"
      :calculatedTax="calculatedTax"
      :grandTotal="grandTotal"
      @submit="handleSubmit"
    />
    <div class="flex-grow-1"></div>
    <MainFooter />
  </div>

  <div
    class="delete-popup offcanvas offcanvas-end border-0"
    tabindex="-1"
    id="deletePopup"
  >
    <div class="offcanvas-body p-0">
      <div class="delete-success">
        <img src="../../assets/img/icons/tick-circle.svg" alt="Image" />
        <span class="text-white fw-medium">
          {{ $t("purchase_page.product_deleted_from_list") }}
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, reactive, computed, watch } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import MainHeader from "../../components/Layouts/MainHeader.vue";
import MainSidebar from "../../components/Layouts/MainSidebar.vue";
import BreadcrumbMenu from "../../components/Common/BreadcrumbMenu.vue";
import SelectedProducts from "../../components/Purchases/CreatePurchase/SelectedProducts.vue";
import SubmitPurchase from "../../components/Purchases/CreatePurchase/SubmitPurchase.vue";
import MainFooter from "../../components/Layouts/MainFooter.vue";

import supplierService, { Supplier } from "../../services/supplier";
import warehouseService, { Warehouse } from "../../services/warehouse";
import settingService from "../../services/setting";
import productService, { Product } from "../../services/product";
import paymentMethodService, { PaymentMethod } from "../../services/paymentMethod";
import * as purchaseService from "../../services/purchase";
import { createPurchase } from "../../services/purchase";
import { useCurrentUser } from "../../composables/useCurrentUser";
import { useRouter } from "vue-router";

type SelectedPurchaseProduct = Product & {
  original_qty?: number;
  quantity: number;
  tax: number;
  discount: number;
  subTotal: string;
};

type StockAwareProduct = Product & {
  original_qty?: number;
};

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeSearchText = (value: string): string =>
  value.replace(/\s+/g, " ").trim();

function normalizeCollection<T>(value: unknown): T[] {
  if (Array.isArray(value)) {
    return value as T[];
  }

  if (value && typeof value === "object") {
    const maybeData = (value as { data?: unknown }).data;
    if (Array.isArray(maybeData)) {
      return maybeData as T[];
    }
  }

  return [];
}

const createLineSubtotal = (product: Partial<SelectedPurchaseProduct>): number => {
  const unitCost = toNumber(product.cost);
  const quantity = toNumber(product.quantity);
  const discount = toNumber(product.discount);
  const tax = toNumber(product.tax);

  return unitCost * quantity - discount + tax;
};

export default defineComponent({
  name: "CreatePurchasePage",
  components: {
    MainHeader,
    MainSidebar,
    BreadcrumbMenu,
    SelectedProducts,
    SubmitPurchase,
    MainFooter,
  },
  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const suppliers = ref<Supplier[]>([]);
    const warehouses = ref<Warehouse[]>([]);
    const paymentMethods = ref<PaymentMethod[]>([]);
    const selectedProducts = ref<SelectedPurchaseProduct[]>([]);
    const searchQuery = ref("");
    const searchResults = ref<Product[]>([]);
    const showResults = ref(false);
    const isSubmitting = ref(false);
    const { fetchCurrentUser } = useCurrentUser();
    const breadcrumbItems = [
      { label: "purchase_list", to: "/purchase-list" },
      { label: "create_purchase" },
    ];
    
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');

    const form = reactive({
      date: `${year}-${month}-${day}`,
      ref: "",
      supplier_id: null as number | null,
      warehouse_id: null as number | null,
      order_tax: 0,
      discount: 0,
      shipping: 0,
      status: "received",
      notes: "",
      paid_amount: 0,
      payment_method_id: null as number | null,
    });

    const normalizedSearchQuery = computed(() => normalizeSearchText(searchQuery.value));

    const subtotal = computed(() => {
      return selectedProducts.value.reduce((acc, product) => {
        return acc + createLineSubtotal(product);
      }, 0);
    });

    const calculatedTax = computed(() => {
      return subtotal.value * (toNumber(form.order_tax) / 100);
    });

    const grandTotal = computed(() => {
      return subtotal.value + calculatedTax.value + toNumber(form.shipping) - toNumber(form.discount);
    });

    const fetchData = async () => {
      const results = await Promise.allSettled([
        supplierService.getSuppliers({ per_page: 1000 }),
        warehouseService.getWarehouses(),
        paymentMethodService.getPaymentMethods(),
        purchaseService.getNextReference(),
        settingService.getSettings(),
        fetchCurrentUser(true),
      ]);

      const [
        suppliersResult,
        warehousesResult,
        paymentMethodsResult,
        nextRefResult,
        settingsResult,
        currentUserResult,
      ] = results;

      if (suppliersResult.status === "fulfilled") {
        suppliers.value = normalizeCollection<Supplier>(suppliersResult.value);
      } else {
        console.error("Failed to load suppliers:", suppliersResult.reason);
      }

      if (warehousesResult.status === "fulfilled") {
        warehouses.value = normalizeCollection<Warehouse>(warehousesResult.value);
      } else {
        console.error("Failed to load warehouses:", warehousesResult.reason);
      }

      if (paymentMethodsResult.status === "fulfilled") {
        paymentMethods.value = normalizeCollection<PaymentMethod>(paymentMethodsResult.value);
      } else {
        console.error("Failed to load payment methods:", paymentMethodsResult.reason);
      }

      if (!form.ref && nextRefResult.status === "fulfilled") {
        form.ref = nextRefResult.value ?? "";
      } else if (nextRefResult.status === "rejected") {
        console.error("Failed to load purchase reference:", nextRefResult.reason);
      }

      const settings =
        settingsResult.status === "fulfilled" ? settingsResult.value : null;
      if (settingsResult.status === "rejected") {
        console.error("Failed to load settings:", settingsResult.reason);
      }

      const currentUser =
        currentUserResult.status === "fulfilled" ? currentUserResult.value : null;
      if (currentUserResult.status === "rejected") {
        console.error("Failed to load current user:", currentUserResult.reason);
      }

      const preferredWarehouseId =
        currentUser?.warehouse_id ?? settings?.default_warehouse ?? null;
      const matchedWarehouse = warehouses.value.find(
        (warehouse) => Number(warehouse.id) === Number(preferredWarehouseId)
      );

      if (matchedWarehouse?.id) {
        form.warehouse_id = matchedWarehouse.id;
      } else if (!form.warehouse_id && warehouses.value.length > 0) {
        form.warehouse_id = warehouses.value[0].id ?? null;
      }

      if (
        suppliersResult.status === "rejected" &&
        warehousesResult.status === "rejected" &&
        paymentMethodsResult.status === "rejected" &&
        nextRefResult.status === "rejected"
      ) {
        await Swal.fire(t("purchase_page.error"), t("purchase_page.failed_to_load_purchase_setup_data"), "error");
      }
    };

    const handleSearch = async () => {
      const searchTerm = normalizedSearchQuery.value;

      if (searchTerm.length < 1) {
        searchResults.value = [];
        showResults.value = false;
        return;
      }

      try {
        const params = {
          type: 1,
          search: searchTerm,
        };

        const products = await productService.getProducts(params);
        const searchNeedle = searchTerm.toLowerCase();

        searchResults.value = products.filter((product) => {
          const productName = normalizeSearchText(product.name || "").toLowerCase();
          const productCode = normalizeSearchText(product.code || "").toLowerCase();

          return productName.includes(searchNeedle) || productCode.includes(searchNeedle);
        });
        showResults.value = true;
      } catch (error) {
        console.error("Error searching products:", error);
        searchResults.value = [];
        showResults.value = true;
      }
    };
    watch(() => form.warehouse_id, () => {
      handleSearch();
      // Update stock for selected products when warehouse changes
      selectedProducts.value.forEach((p) => {
        // Ensure we preserve original total qty
        if (p.original_qty === undefined) {
          p.original_qty = p.qty;
        }
        p.qty = getProductStock(p);
      });
    });

    const getProductStock = (product: StockAwareProduct | SelectedPurchaseProduct) => {
      if (!form.warehouse_id) {
        // If no warehouse selected, return total qty (original_qty if we modified it, else qty)
        return product.original_qty !== undefined ? product.original_qty : (product.qty || 0);
      }
      // If warehouse selected, find specific stock
      if (product.stocks) {
        const stock = product.stocks.find((s) => Number(s.warehouse_id) === Number(form.warehouse_id));
        return stock?.qty || stock?.quantity || 0;
      }
      
      return 0;
    };

    const addProduct = (product: Product) => {
      const existing = selectedProducts.value.find(p => p.id === product.id);
      if (existing) {
        existing.quantity++;
        existing.subTotal = createLineSubtotal(existing).toFixed(2);
      } else {
        // Determine stock to display based on current warehouse selection
        const displayStock = getProductStock(product);
        
        selectedProducts.value.push({
          ...product,
          original_qty: product.qty, // Preserve original total quantity
          qty: displayStock,         // Override qty for display
          quantity: 1,
          tax: 0,
          discount: 0,
          subTotal: createLineSubtotal({
            cost: toNumber(product.cost),
            quantity: 1,
            discount: 0,
            tax: 0,
          }).toFixed(2)
        });
      }
      searchQuery.value = "";
      showResults.value = false;
    };
    const removeProduct = (index: number) => {
      selectedProducts.value.splice(index, 1);
    };
    const handleSubmit = async () => {
      if (selectedProducts.value.length === 0) {
        await Swal.fire(t("purchase_page.error"), t("purchase_page.add_at_least_one_product"), "error");
        return;
      }
      if (!form.supplier_id || !form.warehouse_id) {
        await Swal.fire(t("purchase_page.error"), t("purchase_page.select_supplier_and_warehouse"), "error");
        return;
      }
      isSubmitting.value = true;
      try {
        const selectedWarehouse = warehouses.value.find(w => w.id === form.warehouse_id);
        const selectedSupplier = suppliers.value.find(s => s.id === form.supplier_id);
         const selectedPaymentMethod = paymentMethods.value.find(m => m.id === form.payment_method_id);
 
         let paymentStatus = "pending";
        if (form.paid_amount >= grandTotal.value) {
          paymentStatus = "paid";
        } else if (form.paid_amount > 0) {
          paymentStatus = "partial";
        }
        const payload = {
          ...form,
          ref: form.ref || "PR-" + Math.floor(Date.now() / 1000),
          grand_total: grandTotal.value,
          paid: form.paid_amount,
          method_id: form.payment_method_id,
          paid_by: selectedPaymentMethod ? selectedPaymentMethod.name.toLowerCase() : "cash",
          warehouse_code: selectedWarehouse?.code || "",
          warehouse_name: selectedWarehouse?.name || "",
          supplier_code: selectedSupplier?.code || "",
          supplier_name: selectedSupplier?.name || "",
          payment_status: paymentStatus,
          items: selectedProducts.value.map((p) => {
            const itemSubtotal = createLineSubtotal(p);
            return {
              product_id: p.id,
              product_code: p.code,
              product_name: p.name,
              unit_cost: p.cost,
              quantity: p.quantity,
              tax: p.tax,
              discount: p.discount,
              subtotal: itemSubtotal,
              grand_total: itemSubtotal,
            };
          }),
        };
        await createPurchase(payload as any);
        await Swal.fire(t("purchase_page.success"), t("purchase_page.purchase_created_successfully"), "success");
        router.push("/purchase-list");
      } catch (error: unknown) {
        console.error("Error submitting purchase:", error);
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        const message = err.response?.data?.message || err.message || t("purchase_page.failed_to_create_purchase");
        await Swal.fire(t("purchase_page.error"), message, "error");
      } finally {
        isSubmitting.value = false;
      }
    };
    onMounted(() => {
      fetchData();
    });
    return {
        suppliers,
        warehouses,
        paymentMethods,
        selectedProducts,
        searchQuery,
        searchResults,
        showResults,
        form,
        normalizedSearchQuery,
        subtotal,
        calculatedTax,
        grandTotal,
        handleSearch,
        addProduct,
        removeProduct,
        handleSubmit,
        isSubmitting,
        getProductStock,
        breadcrumbItems,
      };
    },
  });
</script>

<style scoped>
.purchase-form-grid .form-group select {
  width: 100%;
  min-height: 55px;
  padding: 0 16px;
  background: #fff;
}

.purchase-search-block {
  z-index: 5;
}

.search-results-dropdown {
  max-height: 300px;
}

@media (max-width: 991px) {
  .purchase-form-grid {
    margin-bottom: 20px;
  }
}

@media (max-width: 575px) {
  .search-results-dropdown {
    max-height: 240px;
  }
}
</style>
